/* =========================================================
   Testimonial Cards slider
   Progressive enhancement over a CSS scroll-snap track:
   without JS the track still scrolls and swipes natively,
   the arrows simply never appear.
   ========================================================= */

class TestimonialSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-tc-track]');
    this.prev = this.querySelector('[data-tc-prev]');
    this.next = this.querySelector('[data-tc-next]');

    if (!this.track) return;

    this.onScroll = this.update.bind(this);
    this.onResize = this.update.bind(this);

    if (this.prev) {
      this.prev.addEventListener('click', () => this.step(-1));
    }

    if (this.next) {
      this.next.addEventListener('click', () => this.step(1));
    }

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.onResize);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    this.update();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  get distance() {
    const card = this.track.querySelector('[data-tc-card]');
    if (!card) return this.track.clientWidth;

    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;

    return card.getBoundingClientRect().width + gap;
  }

  step(direction) {
    this.track.scrollBy({ left: this.distance * direction, behavior: 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const overflows = max > 1;

    this.toggleAttribute('data-overflow', overflows);

    if (this.prev) this.prev.disabled = !overflows || this.track.scrollLeft <= 1;
    if (this.next) this.next.disabled = !overflows || this.track.scrollLeft >= max - 1;
  }
}

if (!customElements.get('testimonial-slider')) {
  customElements.define('testimonial-slider', TestimonialSlider);
}

/* =========================================================
   Video popup

   Each card with a video carries its own <dialog>. The player
   lives in a <template> and is mounted when the dialog opens,
   then torn out when it closes — so nothing loads up front and
   playback always stops on close.
   ========================================================= */

class TestimonialVideo {
  constructor(card) {
    this.card = card;
    this.dialog = card.querySelector('[data-tc-modal]');
    this.template = card.querySelector('[data-tc-video]');
    this.host = card.querySelector('[data-tc-modal-media]');
    this.trigger = card.querySelector('[data-tc-open]');
    this.closer = card.querySelector('[data-tc-close]');

    if (!this.dialog || !this.template || !this.host || !this.trigger) return;

    this.trigger.addEventListener('click', this.open.bind(this));

    if (this.closer) {
      this.closer.addEventListener('click', () => this.dialog.close());
    }

    // Clicking the backdrop closes; clicks inside the panel must not.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    // Covers the close button, the backdrop and the Esc key in one place.
    this.dialog.addEventListener('close', this.clear.bind(this));
  }

  open() {
    const media = this.template.content.firstElementChild;
    if (!media) return;

    this.host.appendChild(media.cloneNode(true));

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }

    const native = this.host.querySelector('video');
    if (native && typeof native.play === 'function') {
      const started = native.play();
      if (started && typeof started.catch === 'function') {
        started.catch(() => {
          // Autoplay refused — the controls are already visible.
        });
      }
    }
  }

  clear() {
    // Emptying the host stops a playing video and drops any iframe.
    this.host.replaceChildren();
  }
}

function initTestimonialVideos(root) {
  (root || document).querySelectorAll('[data-tc-card]').forEach((card) => {
    if (card.dataset.tcVideoReady === '1') return;
    if (!card.querySelector('[data-tc-modal]')) return;

    card.dataset.tcVideoReady = '1';
    new TestimonialVideo(card);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTestimonialVideos());
} else {
  initTestimonialVideos();
}

// The theme editor re-renders sections and blocks in place.
document.addEventListener('shopify:section:load', (event) => initTestimonialVideos(event.target));
document.addEventListener('shopify:block:select', (event) => initTestimonialVideos(event.target));